import React from "react";
import Button from "../Button";

const APP_STORE =
  "https://apps.apple.com/us/app/social-spectrum/id6473112455";
const PLAY_STORE =
  "https://play.google.com/store/apps/details?id=com.social_spectrum.mobile";

/**
 * Our App hero — design/stitch/our-app.html + our-app.png
 */
const Herosection = () => {
  const handleDownload = (store) => {
    if (store === "apple") {
      window.plausible?.("App Store Click");
      window.open(APP_STORE, "_blank", "noopener,noreferrer");
    } else {
      window.plausible?.("Play Store Click");
      window.open(PLAY_STORE, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section
      className="relative pt-12 pb-20 sm:pb-24 md:pt-16 md:pb-32 text-left overflow-hidden"
      style={{
        background:
          "linear-gradient(to bottom, color-mix(in srgb, var(--color-cloud-mist) 40%, var(--color-white)), var(--color-surface-light), var(--color-white))",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden opacity-40"
        aria-hidden="true"
      >
        <svg
          className="absolute top-12 left-6 sm:left-10 w-8 h-8 text-heart-teal"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          viewBox="0 0 24 24"
        >
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
        <svg
          className="absolute top-20 right-10 sm:right-16 w-7 h-7 text-sunshine"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 flex flex-col items-start">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-surface-white border border-teal-soft mb-5 sm:mb-6 shadow-sm max-w-full">
              <svg
                className="w-4 h-4 text-heart-teal shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              <span className="font-accent-hand text-[20px] sm:text-[22px] leading-snug text-heart-teal">
                Your digital village, always with you
              </span>
            </div>

            <h1 className="font-headline text-[1.75rem] sm:text-4xl lg:text-5xl tracking-tight mb-5 sm:mb-6 text-ink">
              <span className="font-light block text-muted">
                Your Support System
              </span>
              <span className="font-extrabold block text-spectrum-blue">
                in One Place
              </span>
            </h1>

            <p className="font-body text-[15px] sm:text-base md:text-lg text-muted max-w-[540px] mb-7 sm:mb-8 leading-relaxed">
              Connect, share, and grow with families who understand your journey.
            </p>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-8 sm:mb-10">
              <Button href="#download" variant="primary" size="md">
                Get Started →
              </Button>
              <a
                href="#features"
                className="inline-flex items-center justify-center bg-surface-white text-spectrum-blue border border-blue-soft px-6 py-3.5 rounded-brand font-headline font-semibold text-[15px] hover:bg-cloud-mist transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
              >
                Explore Features
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => handleDownload("apple")}
                className="flex items-center gap-3 px-4 py-2 bg-ink text-surface-white rounded-brand hover:opacity-90 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
              >
                <span className="text-left leading-none">
                  <span className="block text-[10px] opacity-80">
                    Download on the
                  </span>
                  <span className="font-headline font-bold text-xs">
                    App Store
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleDownload("play")}
                className="flex items-center gap-3 px-4 py-2 bg-ink text-surface-white rounded-brand hover:opacity-90 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
              >
                <span className="text-left leading-none">
                  <span className="block text-[10px] opacity-80">GET IT ON</span>
                  <span className="font-headline font-bold text-xs">
                    Google Play
                  </span>
                </span>
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center">
            <div className="relative w-full max-w-[460px] aspect-[4/5] rounded-brand overflow-hidden border border-hairline shadow-brand-md bg-surface-white p-2 sm:p-3">
              <div className="w-full h-full rounded-brand overflow-hidden relative">
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/images/photos/app-family-video-call.webp"
                  }
                  alt="Parent and child laughing together during a video call on a phone"
                  className="w-full h-full object-cover rounded-brand"
                />
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 bg-white-chip backdrop-blur-md p-3 sm:p-4 rounded-brand border border-hairline shadow-lg flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-brand bg-cloud-mist flex items-center justify-center text-spectrum-blue shrink-0">
                      <svg
                        fill="none"
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="font-headline text-xs sm:text-sm font-bold text-ink truncate">
                        5,400+ Families
                      </p>
                      <p className="text-[10px] sm:text-xs text-muted-soft truncate">
                        Connected in local circles
                      </p>
                    </div>
                  </div>
                  <span className="shrink-0 px-2.5 py-1 rounded-full bg-sunshine-soft text-ink text-[10px] sm:text-xs font-semibold font-headline">
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Herosection;
